import {capitalize, filter, map} from 'lodash';
import {FormProvider, useForm} from 'react-hook-form';

import ButtonPanel from 'components/Button/Panel';
import FormCancel from 'components/form/Cancel';
import FormCombobox from 'components/form/Combobox';
import FormSubmit from 'components/form/Submit';
import FormText from 'components/form/Text';
import {handleSubmitErrors} from 'components/form/utils';
import useAppContext from 'conf/AppContext';


export default function UserForm({user, techStackOptions, onSave, onClose}) {
  const appContext = useAppContext();

  const languageOptions = filter(techStackOptions, (o) => o.category === 'LANGUAGE');
  const frontendOptions = filter(techStackOptions, (o) => o.category === 'FRONTEND');
  const backendOptions = filter(techStackOptions, (o) => o.category === 'BACKEND');
  const databaseOptions = filter(techStackOptions, (o) => o.category === 'DATABASE');
  const devOpsOptions = filter(techStackOptions, (o) => o.category === 'DEVOPS');
  const aiOptions = filter(techStackOptions, (o) => o.category === 'AI');

  const selectedLanguage = filter(user?.techStacks, (ts) => ts.category === 'LANGUAGE');
  const selectedFrontend = filter(user?.techStacks, (ts) => ts.category === 'FRONTEND');
  const selectedBackend = filter(user?.techStacks, (ts) => ts.category === 'BACKEND');
  const selectedDatabase = filter(user?.techStacks, (ts) => ts.category === 'DATABASE');
  const selectedDevOps = filter(user?.techStacks, (ts) => ts.category === 'DEVOPS');
  const selectedAI = filter(user?.techStacks, (ts) => ts.category === 'AI');


  const formMethods = useForm({
    defaultValues: {
      userId: user?.userId,
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      email: user?.email ?? '',
      gender: user?.gender ? {label: capitalize(user.gender), value: user.gender} : '',
      phone: user?.phone ?? '',
      linkedInUrl: user?.linkedInUrl ?? '',
      language: selectedLanguage?.length > 0
        ? map(selectedLanguage, (l) => ({label: l.techStackName, value: l.techStackName}))
        : [],
      frontend: selectedFrontend?.length > 0
        ? map(selectedFrontend, (l) => ({label: l.techStackName, value: l.techStackName}))
        : [],
      backend: selectedBackend?.length > 0
        ? map(selectedBackend, (l) => ({label: l.techStackName, value: l.techStackName}))
        : [],
      database: selectedDatabase?.length > 0
        ? map(selectedDatabase, (l) => ({label: l.techStackName, value: l.techStackName}))
        : [],
      devOps: selectedDevOps?.length > 0
        ? map(selectedDevOps, (l) => ({label: l.techStackName, value: l.techStackName}))
        : [],
      ai: selectedAI?.length > 0 ? map(selectedAI, (l) => ({label: l.techStackName, value: l.techStackName})) : [],
    },
  });

  const {handleSubmit, setError} = formMethods;

  const onSubmit = async (values) => {
    const techStackNames = [
      ...map(values?.language, (val) => val.value),
      ...map(values?.frontend, (val) => val.value),
      ...map(values?.backend, (val) => val.value),
      ...map(values?.database, (val) => val.value),
      ...map(values?.devOps, (val) => val.value),
      ...map(values?.ai, (val) => val.value),
    ];
    try {
      const rez = await appContext.api.put('api/v1/users/update/all', {
        json: {
          userId: values?.userId,
          firstName: values?.firstName,
          lastName: values?.lastName,
          email: values?.email,
          phone: values?.phone,
          gender: values?.gender?.value,
          linkedInUrl: values?.linkedInUrl,
          techStackNames,
        },
        parseJson: true,
      });
      onSave(rez);
    } catch (err) {
      await handleSubmitErrors(appContext, err, setError);
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <h3>Update User</h3>
        <FormText
          label="First name"
          name="firstName"
          required={true}
          className="mb-3"
        />
        <FormText
          label="Last name"
          name="lastName"
          required={true}
          className="mb-3"
        />
        <FormText
          label="Email address"
          name="email"
          required={true}
          className="mb-3"
        />
        <FormCombobox
          label="Gender"
          name="gender"
          required={true}
          options={[
            {label: 'Male', value: 'MALE'},
            {label: 'Female', value: 'FEMALE'},
          ]}
          isMulti={false}
        />
        <FormText
          label="Phone"
          name="phone"
          required={true}
          className="mb-3"
        />
        <FormText
          label="LinkedIn Profile"
          name="linkedInUrl"
          required={true}
          className="mb-3"
        />
        <FormCombobox
          name="language"
          label="Language"
          isMulti={true}
          options={languageOptions}
          required={true}
        />
        <FormCombobox
          name="frontend"
          label="Frontend"
          isMulti={true}
          options={frontendOptions}
        />
        <FormCombobox
          name="backend"
          label="Backend"
          isMulti={true}
          options={backendOptions}
        />
        <FormCombobox
          name="database"
          label="Database"
          isMulti={true}
          options={databaseOptions}
        />
        <FormCombobox
          name="devOps"
          label="Devops"
          isMulti={true}
          options={devOpsOptions}
        />
        <FormCombobox
          name="ai"
          label="AI"
          isMulti={true}
          options={aiOptions}
        />
        <ButtonPanel
          buttons={[
            <FormSubmit key="save">Save</FormSubmit>,
            <FormCancel key="cancel" actionHandler={onClose}>Cancel</FormCancel>,
          ]}
        />
      </form>
    </FormProvider>
  );
}
